/**
 * 学習計画作成モーダル
 */

import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Typography,
  Divider,
  Switch,
  FormControlLabel,
  Box,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { ja, enUS } from 'date-fns/locale'
import { LearningPlanService } from '../../services/learningPlanService'
import api from '../../services/api'
import {
  CreateLearningPlanRequest,
  LearningPlanResponse,
  PlanFrequency,
  TestDifficulty,
  LearningPlanTargetInput,
} from '../../types/learningPlan'
import TargetSelector from '../schedule/TargetSelector'
import { AssignmentTarget, TargetPreviewResponse } from '../../services/scheduleService'

interface ComplianceRule {
  id: string
  title: string
  category: string
}

interface LearningPlanCreateModalProps {
  open: boolean
  onClose: () => void
  onSuccess: (plan: LearningPlanResponse) => void
}

const LearningPlanCreateModal: React.FC<LearningPlanCreateModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { t, i18n } = useTranslation('learningPlan')
  const [form, setForm] = useState<Omit<CreateLearningPlanRequest, 'targets'>>({
    title: '',
    description: '',
    complianceRuleId: '',
    userGroupId: '',
    frequency: 'WEEKLY',
    customIntervalDays: undefined,
    questionCount: 5,
    difficulty: 'MEDIUM',
    passingScore: 80,
    startDate: '',
    endDate: '',
    testGenerationDaysBefore: 3,
    requireApproval: false,
    autoPublish: true,
  })
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [complianceRules, setComplianceRules] = useState<ComplianceRule[]>([])
  const [loadingData, setLoadingData] = useState(false)

  // 対象選択（新方式）
  const [targets, setTargets] = useState<AssignmentTarget[]>([])
  const [targetPreview, setTargetPreview] = useState<TargetPreviewResponse | null>(null)

  const loadInitialData = useCallback(async () => {
    setLoadingData(true)
    try {
      const rulesResponse = await api.get<{ rules: ComplianceRule[] }>('/compliance')
      setComplianceRules(rulesResponse.rules || [])
    } catch (err) {
      console.error('データ読み込みエラー:', err)
      setError(t('messages.loadDetailError'))
    } finally {
      setLoadingData(false)
    }
  }, [t])

  useEffect(() => {
    if (open) {
      loadInitialData()
    }
  }, [open, loadInitialData])

  const handleClose = () => {
    if (!saving) {
      setForm({
        title: '',
        description: '',
        complianceRuleId: '',
        userGroupId: '',
        frequency: 'WEEKLY',
        customIntervalDays: undefined,
        questionCount: 5,
        difficulty: 'MEDIUM',
        passingScore: 80,
        startDate: '',
        endDate: '',
        testGenerationDaysBefore: 3,
        requireApproval: false,
        autoPublish: true,
      })
      setStartDate(null)
      setEndDate(null)
      setTargets([])
      setTargetPreview(null)
      setError('')
      onClose()
    }
  }

  // AssignmentTargetからLearningPlanTargetInputに変換
  const convertTargetsToInput = (
    assignmentTargets: AssignmentTarget[]
  ): LearningPlanTargetInput[] => {
    return assignmentTargets.map((target) => ({
      type: target.type.toUpperCase() as LearningPlanTargetInput['type'],
      ids: target.ids,
    }))
  }

  const handleSave = async () => {
    // バリデーション
    if (!form.title.trim()) {
      setError(t('validation.titleRequired'))
      return
    }
    if (!form.complianceRuleId) {
      setError(t('validation.complianceRuleRequired'))
      return
    }
    // 対象選択のバリデーション（新方式）
    if (targets.length === 0) {
      setError(t('validation.targetRequired') || '対象を選択してください')
      return
    }
    if (!startDate || !endDate) {
      setError(t('validation.datesRequired'))
      return
    }
    if (startDate >= endDate) {
      setError(t('validation.endDateAfterStart'))
      return
    }
    if (form.frequency === 'CUSTOM' && (!form.customIntervalDays || form.customIntervalDays < 1)) {
      setError(t('validation.customIntervalRequired'))
      return
    }

    try {
      setSaving(true)
      setError('')

      const requestData: CreateLearningPlanRequest = {
        title: form.title,
        description: form.description,
        complianceRuleId: form.complianceRuleId,
        targets: convertTargetsToInput(targets), // 新方式で対象を送信
        frequency: form.frequency,
        customIntervalDays: form.customIntervalDays,
        questionCount: form.questionCount,
        difficulty: form.difficulty,
        passingScore: form.passingScore,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        testGenerationDaysBefore: form.testGenerationDaysBefore,
        requireApproval: form.requireApproval,
        autoPublish: form.autoPublish,
      }

      const plan = await LearningPlanService.createPlan(requestData)
      onSuccess(plan)
      handleClose()
    } catch (err: unknown) {
      console.error('プラン作成エラー:', err)
      const errorMessage =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        t('messages.createError')
      setError(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const dateLocale = i18n.language === 'ja' ? ja : enUS

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth disableEscapeKeyDown={saving}>
      <DialogTitle>{t('createModal.title')}</DialogTitle>
      <DialogContent dividers>
        {loadingData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
            <Stack spacing={3} sx={{ mt: 1 }}>
              {error && <Alert severity="error">{error}</Alert>}

              {/* 基本情報 */}
              <Typography variant="subtitle1" fontWeight="bold">
                {t('createModal.basicInfo')}
              </Typography>
              <TextField
                label={t('createModal.planName')}
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
                fullWidth
                disabled={saving}
              />
              <TextField
                label={t('createModal.description')}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                multiline
                rows={2}
                fullWidth
                disabled={saving}
              />
              <FormControl fullWidth required disabled={saving}>
                <InputLabel>{t('createModal.targetComplianceRule')}</InputLabel>
                <Select
                  value={form.complianceRuleId}
                  label={t('createModal.targetComplianceRule')}
                  onChange={(e) => setForm({ ...form, complianceRuleId: e.target.value })}
                >
                  {complianceRules.map((rule) => (
                    <MenuItem key={rule.id} value={rule.id}>
                      {rule.title} ({rule.category})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Divider />

              {/* 対象選択（新方式） */}
              <Typography variant="subtitle1" fontWeight="bold">
                {t('createModal.targetSelection') || '対象選択'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('createModal.targetSelectionHelp') ||
                  'グループ、組織、役職、個人から対象を選択してください。複数のタイプを組み合わせることができます。'}
              </Typography>
              <TargetSelector
                targets={targets}
                onTargetsChange={setTargets}
                preview={targetPreview}
                onPreviewUpdate={setTargetPreview}
                disabled={saving}
              />

              <Divider />

              {/* スケジュール設定 */}
              <Typography variant="subtitle1" fontWeight="bold">
                {t('createModal.scheduleSettings')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <DatePicker
                  label={t('createModal.startDate')}
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  disabled={saving}
                  slotProps={{
                    textField: { required: true, sx: { flex: 1, minWidth: 200 } },
                  }}
                />
                <DatePicker
                  label={t('createModal.endDate')}
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  disabled={saving}
                  minDate={startDate || undefined}
                  slotProps={{
                    textField: { required: true, sx: { flex: 1, minWidth: 200 } },
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl sx={{ flex: 1, minWidth: 150 }} disabled={saving}>
                  <InputLabel>{t('createModal.testFrequency')}</InputLabel>
                  <Select
                    value={form.frequency}
                    label={t('createModal.testFrequency')}
                    onChange={(e) =>
                      setForm({ ...form, frequency: e.target.value as PlanFrequency })
                    }
                  >
                    <MenuItem value="WEEKLY">{t('frequency.WEEKLY')}</MenuItem>
                    <MenuItem value="BIWEEKLY">{t('frequency.BIWEEKLY')}</MenuItem>
                    <MenuItem value="MONTHLY">{t('frequency.MONTHLY')}</MenuItem>
                    <MenuItem value="CUSTOM">{t('frequency.CUSTOM')}</MenuItem>
                  </Select>
                </FormControl>
                {form.frequency === 'CUSTOM' && (
                  <TextField
                    label={t('createModal.intervalDays')}
                    type="number"
                    value={form.customIntervalDays || ''}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        customIntervalDays: parseInt(e.target.value, 10) || undefined,
                      })
                    }
                    inputProps={{ min: 1 }}
                    sx={{ flex: 1, minWidth: 120 }}
                    disabled={saving}
                  />
                )}
                <TextField
                  label={t('createModal.testGenerationDays')}
                  type="number"
                  value={form.testGenerationDaysBefore}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      testGenerationDaysBefore: parseInt(e.target.value, 10) || 3,
                    })
                  }
                  inputProps={{ min: 1, max: 30 }}
                  sx={{ flex: 1, minWidth: 150 }}
                  disabled={saving}
                  helperText={t('createModal.testGenerationDaysHelp')}
                />
              </Box>

              <Divider />

              {/* テスト設定 */}
              <Typography variant="subtitle1" fontWeight="bold">
                {t('createModal.testSettings')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label={t('createModal.questionCount')}
                  type="number"
                  value={form.questionCount}
                  onChange={(e) =>
                    setForm({ ...form, questionCount: parseInt(e.target.value, 10) || 5 })
                  }
                  inputProps={{ min: 1, max: 20 }}
                  sx={{ flex: 1, minWidth: 100 }}
                  disabled={saving}
                />
                <FormControl sx={{ flex: 1, minWidth: 120 }} disabled={saving}>
                  <InputLabel>{t('createModal.difficulty')}</InputLabel>
                  <Select
                    value={form.difficulty}
                    label={t('createModal.difficulty')}
                    onChange={(e) =>
                      setForm({ ...form, difficulty: e.target.value as TestDifficulty })
                    }
                  >
                    <MenuItem value="EASY">{t('difficulty.EASY')}</MenuItem>
                    <MenuItem value="MEDIUM">{t('difficulty.MEDIUM')}</MenuItem>
                    <MenuItem value="HARD">{t('difficulty.HARD')}</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label={t('createModal.passingScore')}
                  type="number"
                  value={form.passingScore}
                  onChange={(e) =>
                    setForm({ ...form, passingScore: parseInt(e.target.value, 10) || 80 })
                  }
                  inputProps={{ min: 0, max: 100 }}
                  sx={{ flex: 1, minWidth: 100 }}
                  disabled={saving}
                />
              </Box>

              <Divider />

              {/* 承認設定 */}
              <Typography variant="subtitle1" fontWeight="bold">
                {t('createModal.approvalSettings')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.requireApproval}
                      onChange={(e) => setForm({ ...form, requireApproval: e.target.checked })}
                      disabled={saving}
                    />
                  }
                  label={t('createModal.requireApproval')}
                />
                <Typography variant="caption" color="textSecondary" sx={{ ml: 6 }}>
                  {t('createModal.requireApprovalHelp')}
                </Typography>
              </Box>
              {!form.requireApproval && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.autoPublish}
                        onChange={(e) => setForm({ ...form, autoPublish: e.target.checked })}
                        disabled={saving}
                      />
                    }
                    label={t('createModal.autoPublish')}
                  />
                  <Typography variant="caption" color="textSecondary" sx={{ ml: 6 }}>
                    {t('createModal.autoPublishHelp')}
                  </Typography>
                </Box>
              )}
            </Stack>
          </LocalizationProvider>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          {t('createModal.cancel')}
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || loadingData}>
          {saving ? <CircularProgress size={24} /> : t('createModal.create')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default LearningPlanCreateModal
